import { ApiError, apiRequest, getAccessToken, tryRefresh } from "./client";
import { getRequestId } from "../lib/log";

// Зеркало backend ``schemas/analytics``. Дублирование лучше, чем кросс-стек
// codegen на этом этапе MVP — типов мало, ломать sync будут изменения PRD.

export interface TimelineEvent {
  event_type: string;
  page_number: number | null;
  payload: Record<string, unknown> | null;
  client_ts: string;
  created_at: string;
}

export interface TimelineSession {
  id: string;
  started_at: string;
  last_activity_at: string;
  ended_at: string | null;
  aggregated_at: string | null;
  device_uuid: string;
  is_new_device: boolean;
  partial_match: boolean;
  counted_pages: number[];
  exit_page: number | null;
  total_active_seconds: number;
  cta_clicked_at: string | null;
  print_initiated_at: string | null;
  downloaded_at: string | null;
  ip_hash: string | null;
  user_agent: string | null;
  events: TimelineEvent[];
}

export interface DispatchTimelineResponse {
  dispatch_id: string;
  document_id: string;
  sessions: TimelineSession[];
  truncated_events: boolean;
}

export type HeatmapLayer = "clicks" | "attention" | "both";

export interface HeatmapClick {
  x: number;
  y: number;
}

export interface HeatmapAttentionBlock {
  block_index: number;
  total_attention_ms: number;
  sample_count: number;
}

export interface HeatmapPage {
  page: number;
  clicks: HeatmapClick[];
  attention: HeatmapAttentionBlock[];
}

export interface HeatmapResponse {
  layer: HeatmapLayer;
  document_id: string;
  dispatch_id: string | null;
  page_filter: number | null;
  date_from: string | null;
  date_to: string | null;
  pages: HeatmapPage[];
  unique_sessions: boolean;
}

interface HeatmapQuery {
  layer?: HeatmapLayer;
  page?: number;
  date_from?: string;
  date_to?: string;
  /** PRD §7.5: дедуп кликов и attention по сессии. */
  unique_sessions?: boolean;
}

function buildHeatmapQuery(q: HeatmapQuery): string {
  const params = new URLSearchParams();
  if (q.layer) params.set("layer", q.layer);
  if (q.page !== undefined) params.set("page", String(q.page));
  if (q.date_from) params.set("date_from", q.date_from);
  if (q.date_to) params.set("date_to", q.date_to);
  if (q.unique_sessions) params.set("unique_sessions", "true");
  const s = params.toString();
  return s ? `?${s}` : "";
}

// --- Reports ---

export type PeriodGranularity = "day" | "week" | "month";

export interface PeriodReportBucket {
  bucket_start: string;
  dispatches_created: number;
  sessions_started: number;
  hot_leads: number;
  avg_depth: number;
}

export interface PeriodReportResponse {
  granularity: PeriodGranularity;
  date_from: string;
  date_to: string;
  buckets: PeriodReportBucket[];
}

export interface DocumentReportItem {
  document_id: string;
  title: string;
  total_dispatches: number;
  total_sessions: number;
  total_counted_pages: number;
  avg_depth: number;
  hot_leads: number;
}

export interface DocumentsReportResponse {
  items: DocumentReportItem[];
}

export interface ManagerReportItem {
  manager_user_id: string;
  full_name: string;
  email: string;
  total_dispatches: number;
  total_sessions: number;
  avg_depth: number;
  hot_leads: number;
}

export interface ManagersReportResponse {
  items: ManagerReportItem[];
}

export interface PeriodReportQuery {
  date_from: string;
  date_to: string;
  granularity?: PeriodGranularity;
}

function buildPeriodQuery(q: PeriodReportQuery): string {
  const params = new URLSearchParams();
  params.set("date_from", q.date_from);
  params.set("date_to", q.date_to);
  if (q.granularity) params.set("granularity", q.granularity);
  return `?${params.toString()}`;
}

export type ReportKind = "period" | "documents" | "managers";

// --- CSV download ---

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

async function rawCsvFetch(path: string): Promise<Response> {
  const headers: Record<string, string> = {
    "X-Request-ID": getRequestId(),
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(`${API_BASE}${path}`, { headers, credentials: "include" });
}

async function csvParseError(res: Response): Promise<ApiError> {
  let code = `http.${res.status}`;
  let message = res.statusText;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string };
    };
    if (body?.error?.code) code = body.error.code;
    if (body?.error?.message) message = body.error.message;
  } catch {
    /* пустое тело — оставляем заголовки HTTP-статуса */
  }
  return new ApiError(res.status, code, message);
}

/**
 * Скачивает CSV-ответ по path и сохраняет его в файл filename.
 *
 * 1. Делает аутентифицированный GET (+ silent refresh на 401);
 * 2. Превращает body в Blob;
 * 3. Создаёт скрытую <a> со ссылкой на blob: URL и кликает.
 *
 * ``saveBlob`` инъекционен — для тестов, чтобы не дёргать настоящий DOM.
 */
export interface DownloadCsvOptions {
  saveBlob?: (blob: Blob, filename: string) => void;
}

function defaultSaveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadCsv(
  path: string,
  filename: string,
  options: DownloadCsvOptions = {},
): Promise<void> {
  let res = await rawCsvFetch(path);
  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) res = await rawCsvFetch(path);
  }
  if (!res.ok) throw await csvParseError(res);
  const blob = await res.blob();
  (options.saveBlob ?? defaultSaveBlob)(blob, filename);
}

export const analyticsApi = {
  dispatchTimeline: (dispatchId: string) =>
    apiRequest<DispatchTimelineResponse>(
      `/analytics/dispatches/${dispatchId}/timeline`,
    ),
  dispatchHeatmap: (dispatchId: string, query: HeatmapQuery = {}) =>
    apiRequest<HeatmapResponse>(
      `/analytics/dispatches/${dispatchId}/heatmap${buildHeatmapQuery(query)}`,
    ),
  documentHeatmap: (documentId: string, query: HeatmapQuery = {}) =>
    apiRequest<HeatmapResponse>(
      `/analytics/documents/${documentId}/heatmap${buildHeatmapQuery(query)}`,
    ),
  periodReport: (query: PeriodReportQuery) =>
    apiRequest<PeriodReportResponse>(
      `/analytics/reports/period${buildPeriodQuery(query)}`,
    ),
  documentsReport: () =>
    apiRequest<DocumentsReportResponse>("/analytics/reports/documents"),
  managersReport: () =>
    apiRequest<ManagersReportResponse>("/analytics/reports/managers"),
  exportPeriodCsv: (
    query: PeriodReportQuery,
    filename: string,
    options?: DownloadCsvOptions,
  ) =>
    downloadCsv(
      `/analytics/reports/period/export.csv${buildPeriodQuery(query)}`,
      filename,
      options,
    ),
  exportDocumentsCsv: (filename: string, options?: DownloadCsvOptions) =>
    downloadCsv(
      "/analytics/reports/documents/export.csv",
      filename,
      options,
    ),
  exportManagersCsv: (filename: string, options?: DownloadCsvOptions) =>
    downloadCsv(
      "/analytics/reports/managers/export.csv",
      filename,
      options,
    ),
};
